import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  SelfImprovement,
  Psychology,
  AutoAwesome,
  Diamond,
  Star,
  ExpandMore,
  WhatsApp,
  Phone,
} from '@mui/icons-material';
import { useTranslation } from '../hooks/useTranslation';

const Services = () => {
  const { t, language } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const localized = (hi, en) => ({ hi, en });

  const getText = (value) => {
    if (typeof value === 'string') {
      return value;
    }

    if (value && typeof value === 'object') {
      if (value[language]) return value[language];
      if (value.en) return value.en;
      if (value.hi) return value.hi;
    }

    return '';
  };

  const pageCopy = {
    subtitle: localized(
      'हमारी सभी सेवाओं का विस्तृत विवरण और उनके लाभ',
      'Detailed overview of all our services and their benefits'
    ),
    benefitsHeading: localized('मुख्य लाभ:', 'Key Benefits:'),
    callNow: localized('कॉल करें', 'Call Now'),
    whatsappCta: localized('WhatsApp करें', 'Message on WhatsApp'),
    faqTitle: localized('सामान्य प्रश्न', 'Frequently Asked Questions'),
    contactHeading: localized(
      'परामर्श लेने के लिए संपर्क करें',
      'Contact Us for a Consultation'
    ),
    contactDescription: localized(
      'हमारे विशेषज्ञ आपकी सभी समस्याओं का समाधान करने के लिए तैयार हैं',
      'Our experts are ready to resolve all your concerns'
    ),
    phoneButton: localized('फोन करें', 'Call Now'),
    faqItems: [
      {
        question: localized('क्या आपकी सेवाएं प्रामाणिक हैं?', 'Are your services authentic?'),
        answer: localized(
          'हां, हमारी सभी सेवाएं वैदिक शास्त्रों और पुराणों के अनुसार की जाती हैं। हमारे पास 15+ वर्षों का अनुभव है।',
          'Yes, every service is performed according to Vedic scriptures and Puranas. We have 15+ years of experience.'
        ),
      },
      {
        question: localized('कितने समय में परिणाम दिखते हैं?', 'How long does it take to see results?'),
        answer: localized(
          'परिणाम आपकी समस्या और किए गए उपाय पर निर्भर करते हैं। सामान्यतः 21 से 40 दिनों में सकारात्मक परिवर्तन दिखने लगते हैं।',
          'Results depend on your concern and the prescribed remedies. Generally, positive changes appear within 21 to 40 days.'
        ),
      },
      {
        question: localized('क्या रत्न प्रमाणित होते हैं?', 'Are the gemstones certified?'),
        answer: localized(
          'हां, हमारे सभी रत्न प्रमाणित हैं और भारतीय मानकों के अनुसार जांचे गए हैं। प्रत्येक रत्न के साथ प्रमाण पत्र दिया जाता है।',
          'Yes, every gemstone is certified and tested as per Indian standards, and each comes with a certificate.'
        ),
      },
      {
        question: localized('क्या ऑनलाइन परामर्श उपलब्ध है?', 'Do you offer online consultations?'),
        answer: localized(
          'हां, हम वीडियो कॉल, फोन कॉल और WhatsApp के माध्यम से ऑनलाइन परामर्श भी प्रदान करते हैं।',
          'Yes, we provide online consultations through video calls, phone calls, and WhatsApp.'
        ),
      },
    ],
  };

  const serviceCategories = [
    {
      id: 'puja',
      title: t('services.pujaTitle'),
      icon: <SelfImprovement />,
      color: '#FF6B35',
      services: [
        {
          name: t('services.pujas.rudrabhishek'),
          description: localized(
            'भगवान शिव का रुद्राभिषेक पूजा से सभी पापों का नाश और मनोकामना पूर्ति।',
            'A powerful Rudrabhishek Puja dedicated to Lord Shiva for cleansing sins and fulfilling wishes.'
          ),
          benefits: [
            localized('पाप मुक्ति', 'Removes accumulated sins'),
            localized('मनोकामना पूर्ति', 'Supports wish fulfilment'),
            localized('आध्यात्मिक उन्नति', 'Enhances spiritual growth'),
            localized('पारिवारिक कल्याण', 'Promotes family well-being'),
          ],
          duration: localized('4-5 घंटे', '4-5 hours'),
          price: localized('₹7,100', '₹7,100'),
        },
        {
          name: t('services.pujas.vastuPujan'),
          description: localized(
            'वास्तु दोष निवारण और घर में सकारात्मक ऊर्जा लाने के लिए विशेष पूजा।',
            'Special Puja to remove Vastu doshas and invite positive energy into the home.'
          ),
          benefits: [
            localized('वास्तु दोष निवारण', 'Corrects Vastu imbalances'),
            localized('सकारात्मक ऊर्जा', 'Attracts positive energy'),
            localized('घर में शांति', 'Brings peace at home'),
            localized('समृद्धि वृद्धि', 'Improves prosperity'),
          ],
          duration: localized('2-3 घंटे', '2-3 hours'),
          price: localized('₹3,100', '₹3,100'),
        },
        {
          name: t('services.pujas.vastuShanti'),
          description: localized(
            'वास्तु शांति पूजा से घर और कार्यक्षेत्र में शांति और समृद्धि आती है।',
            'Vastu Shanti Puja to harmonise home and workplace energies for peace and prosperity.'
          ),
          benefits: [
            localized('वास्तु शांति', 'Balances Vastu energies'),
            localized('पारिवारिक सुख', 'Creates family harmony'),
            localized('व्यापारिक वृद्धि', 'Supports business growth'),
            localized('मानसिक शांति', 'Provides mental peace'),
          ],
          duration: localized('3-4 घंटे', '3-4 hours'),
          price: localized('₹4,100', '₹4,100'),
        },
        {
          name: t('services.pujas.bhagavatKatha'),
          description: localized(
            'श्री मद भागवत कथा से आध्यात्मिक लाभ और जीवन में सकारात्मकता आती है।',
            'Shri Mad Bhagavat Katha for profound spiritual upliftment and lasting positivity in life.'
          ),
          benefits: [
            localized('आध्यात्मिक विकास', 'Deepens spiritual evolution'),
            localized('पाप मुक्ति', 'Assists in karmic cleansing'),
            localized('मानसिक शुद्धता', 'Purifies the mind'),
            localized('भक्ति भावना', 'Strengthens devotion'),
          ],
          duration: localized('7 दिन', '7 days'),
          price: localized('₹21,000', '₹21,000'),
        },
        {
          name: t('services.pujas.bhagavatParayan'),
          description: localized(
            'भागवत परायण से भगवान की कृपा और जीवन में सुख-समृद्धि प्राप्त होती है।',
            'Bhagavat Parayan to invoke divine grace and invite joy and abundance into life.'
          ),
          benefits: [
            localized('भगवान की कृपा', 'Blessings of the Divine'),
            localized('सुख-समृद्धि', 'Brings joy and abundance'),
            localized('आध्यात्मिक शांति', 'Offers spiritual calm'),
            localized('मोक्ष प्राप्ति', 'Supports liberation'),
          ],
          duration: localized('11 दिन', '11 days'),
          price: localized('₹31,000', '₹31,000'),
        },
        {
          name: t('services.pujas.mulShanti'),
          description: localized(
            'मूल नक्षत्र में जन्मे व्यक्तियों के लिए विशेष शांति पूजा।',
            'Special Shanti Puja for individuals born in Mool Nakshatra to reduce associated challenges.'
          ),
          benefits: [
            localized('मूल दोष निवारण', 'Remedies Mool dosha'),
            localized('आयु वृद्धि', 'Supports longevity'),
            localized('स्वास्थ्य लाभ', 'Improves health'),
            localized('जीवन में सुख', 'Brings happiness in life'),
          ],
          duration: localized('3-4 घंटे', '3-4 hours'),
          price: localized('₹5,100', '₹5,100'),
        },
        {
          name: t('services.pujas.mahamrityunjay'),
          description: localized(
            'महामृत्युंजय मंत्र जाप से मृत्यु भय निवारण और दीर्घायु प्राप्त होती है।',
            'Mahamrityunjay Mantra chanting to dispel fear of untimely death and bless with longevity.'
          ),
          benefits: [
            localized('मृत्यु भय निवारण', 'Removes fear of death'),
            localized('दीर्घायु प्राप्ति', 'Bestows longevity'),
            localized('स्वास्थ्य सुधार', 'Improves overall health'),
            localized('रोग मुक्ति', 'Promotes healing'),
          ],
          duration: localized('1.25 लाख जप', '1.25 lakh chants'),
          price: localized('₹11,000', '₹11,000'),
        },
        {
          name: t('services.pujas.kalSarpShanti'),
          description: localized(
            'काल सर्प दोष शांति पूजा से जीवन की सभी बाधाएं दूर होती हैं।',
            'Kal Sarp Shanti Puja to neutralize Kal Sarp Dosha and remove life obstacles.'
          ),
          benefits: [
            localized('काल सर्प दोष निवारण', 'Removes Kal Sarp dosha'),
            localized('करियर में सफलता', 'Aids career success'),
            localized('विवाह में सहायक', 'Supports marriage prospects'),
            localized('मानसिक शांति', 'Brings peace of mind'),
          ],
          duration: localized('5-6 घंटे', '5-6 hours'),
          price: localized('₹7,100', '₹7,100'),
        },
        {
          name: t('services.pujas.grahShanti'),
          description: localized(
            'सभी ग्रहों की शांति के लिए विशेष पूजा जो जीवन में संतुलन लाती है।',
            'Comprehensive Graha Shanti Puja to pacify all planets and restore life balance.'
          ),
          benefits: [
            localized('सभी ग्रह शांति', 'Calms all planetary influences'),
            localized('भाग्योदय', 'Boosts fortune'),
            localized('जीवन संतुलन', 'Brings life balance'),
            localized('सफलता प्राप्ति', 'Promotes success'),
          ],
          duration: localized('4-5 घंटे', '4-5 hours'),
          price: localized('₹6,100', '₹6,100'),
        },
        {
          name: t('services.pujas.pitraDoshShanti'),
          description: localized(
            'पितृ दोष शांति पूजा से पूर्वजों की आत्मा को शांति और वंश की उन्नति होती है।',
            "Pitra Dosh Shanti to appease ancestors' souls and uplift family lineage."
          ),
          benefits: [
            localized('पितृ दोष निवारण', 'Removes ancestral dosha'),
            localized('पूर्वजों की शांति', 'Grants peace to ancestors'),
            localized('वंश वृद्धि', 'Advances lineage growth'),
            localized('पारिवारिक सुख', 'Encourages family happiness'),
          ],
          duration: localized('3-4 घंटे', '3-4 hours'),
          price: localized('₹5,100', '₹5,100'),
        },
        {
          name: t('services.pujas.vivahRukawat'),
          description: localized(
            'विवाह में आने वाली रुकावटों को दूर करने के लिए विशेष पूजा और उपाय।',
            'Special Puja and remedies to remove obstacles delaying marriage.'
          ),
          benefits: [
            localized('विवाह में सफलता', 'Enhances marriage success'),
            localized('रुकावट निवारण', 'Removes delays and hurdles'),
            localized('उपयुक्त वर/वधु', 'Attracts suitable partner'),
            localized('पारिवारिक सहमति', 'Encourages family support'),
          ],
          duration: localized('2-3 घंटे', '2-3 hours'),
          price: localized('₹4,100', '₹4,100'),
        },
        {
          name: t('services.pujas.sammohanKriya'),
          description: localized(
            'सम्मोहन क्रिया से व्यक्तित्व विकास और आकर्षण शक्ति में वृद्धि होती है।',
            'Sammohan Kriya to enhance personality and magnetic influence.'
          ),
          benefits: [
            localized('व्यक्तित्व विकास', 'Improves personality'),
            localized('आकर्षण शक्ति', 'Boosts personal charisma'),
            localized('सामाजिक प्रभाव', 'Increases social impact'),
            localized('आत्मविश्वास', 'Builds self-confidence'),
          ],
          duration: localized('1-2 घंटे', '1-2 hours'),
          price: localized('₹3,100', '₹3,100'),
        },
        {
          name: t('services.pujas.allPujan'),
          description: localized(
            'सभी प्रकार की पूजा-पाठ और धार्मिक अनुष्ठान आपकी आवश्यकता अनुसार।',
            'All kinds of Puja and religious rituals tailored to your specific needs.'
          ),
          benefits: [
            localized('सभी पूजा सेवा', 'Complete Puja services'),
            localized('व्यक्तिगत आवश्यकता', 'Customised rituals'),
            localized('धार्मिक अनुष्ठान', 'Traditional ceremonies'),
            localized('आध्यात्मिक लाभ', 'Spiritual benefits'),
          ],
          duration: localized('आवश्यकतानुसार', 'As required'),
          price: localized('संपर्क करें', 'Contact for pricing'),
        }
      ]
    },
    {
      id: 'astrology',
      title: t('services.astroTitle'),
      icon: <Psychology />,
      color: '#1976D2',
      services: [
        {
          name: t('services.astrology.kundaliAnalysis'),
          description: localized(
            'जन्म कुंडली का विस्तृत विश्लेषण और भविष्य की भविष्यवाणी।',
            'Detailed analysis of your birth chart with accurate future predictions.'
          ),
          benefits: [
            localized('व्यक्तित्व विश्लेषण', 'Personal trait analysis'),
            localized('भविष्य की जानकारी', 'Insights into future events'),
            localized('करियर गाइडेंस', 'Career guidance'),
            localized('स्वास्थ्य सुझाव', 'Health recommendations'),
          ],
          duration: localized('45-60 मिनट', '45-60 minutes'),
          price: localized('₹1,500', '₹1,500'),
        },
        {
          name: t('services.astrology.kundaliBanwana'),
          description: localized(
            'जन्म विवरण के आधार पर सटीक और विस्तृत कुंडली निर्माण।',
            'Accurate and comprehensive horoscope creation based on birth details.'
          ),
          benefits: [
            localized('सटीक कुंडली', 'Precise horoscope'),
            localized('सभी योग विश्लेषण', 'Full yoga analysis'),
            localized('दशा विवरण', 'Mahadasha timelines'),
            localized('ग्रह स्थिति', 'Planetary positions'),
          ],
          duration: localized('24-48 घंटे', '24-48 hours'),
          price: localized('₹1,100', '₹1,100'),
        },
        {
          name: t('services.astrology.ganitJyoti'),
          description: localized(
            'गणितीय ज्योतिष के आधार पर सटीक भविष्यवाणी और विश्लेषण।',
            'Scientific astrology based on mathematical calculations for accurate predictions.'
          ),
          benefits: [
            localized('गणितीय गणना', 'Mathematical calculations'),
            localized('सटीक भविष्यवाणी', 'Highly precise forecasts'),
            localized('वैज्ञानिक पद्धति', 'Scientific approach'),
            localized('विश्वसनीय परिणाम', 'Reliable outcomes'),
          ],
          duration: localized('60-90 मिनट', '60-90 minutes'),
          price: localized('₹2,100', '₹2,100'),
        },
        {
          name: t('services.astrology.matching'),
          description: localized(
            'विवाह के लिए कुंडली मिलान और गुण मिलान की सेवा।',
            'Horoscope matching service to ensure a harmonious marriage.'
          ),
          benefits: [
            localized('सफल वैवाहिक जीवन', 'Ensures marital harmony'),
            localized('गुण मिलान', 'Gun Milan score analysis'),
            localized('दोष निवारण', 'Dosha remedies'),
            localized('शुभ मुहूर्त', 'Auspicious muhurat guidance'),
          ],
          duration: localized('30-45 मिनट', '30-45 minutes'),
          price: localized('₹1,100', '₹1,100'),
        },
        {
          name: t('services.astrology.career'),
          description: localized(
            'करियर और व्यवसाय संबंधी ज्योतिषीय सलाह और मार्गदर्शन।',
            'Astrological guidance for career and business decisions.'
          ),
          benefits: [
            localized('सही करियर चुनाव', 'Helps choose the right career'),
            localized('व्यापारिक सफलता', 'Boosts business success'),
            localized('नौकरी में प्रगति', 'Supports job advancement'),
            localized('आय में वृद्धि', 'Aids financial growth'),
          ],
          duration: localized('30-45 मिनट', '30-45 minutes'),
          price: localized('₹1,200', '₹1,200'),
        },
        {
          name: t('services.astrology.health'),
          description: localized(
            'स्वास्थ्य संबंधी समस्याओं का ज्योतिषीय विश्लेषण और उपाय।',
            'Astrological analysis and remedies for health challenges.'
          ),
          benefits: [
            localized('स्वास्थ्य सुधार', 'Improves overall health'),
            localized('रोग निदान', 'Detects root causes'),
            localized('दवा सुझाव', 'Suggests supportive remedies'),
            localized('जीवनशैली सुधार', 'Guides lifestyle changes'),
          ],
          duration: localized('30-45 मिनट', '30-45 minutes'),
          price: localized('₹1,100', '₹1,100'),
        },
        {
          name: t('services.astrology.business'),
          description: localized(
            'व्यापार और निवेश के लिए ज्योतिषीय सलाह और शुभ मुहूर्त।',
            'Astrological insights for business growth and investment timing.'
          ),
          benefits: [
            localized('व्यापारिक लाभ', 'Increases business gains'),
            localized('सही समय का चुनाव', 'Identifies right timing'),
            localized('निवेश सुझाव', 'Gives investment guidance'),
            localized('भागीदारी सलाह', 'Advises on partnerships'),
          ],
          duration: localized('45-60 मिनट', '45-60 minutes'),
          price: localized('₹1,800', '₹1,800'),
        },
        {
          name: t('services.astrology.remedies'),
          description: localized(
            'ज्योतिषीय उपचार और रत्न सुझाव के साथ समस्या निवारण।',
            'Personalised astrological remedies with gemstone and mantra suggestions.'
          ),
          benefits: [
            localized('दोष निवारण', 'Removes astrological doshas'),
            localized('रत्न सुझाव', 'Recommends suitable gemstones'),
            localized('मंत्र जाप', 'Provides mantra chanting'),
            localized('पूजा विधान', 'Includes ritual guidance'),
          ],
          duration: localized('60-90 मिनट', '60-90 minutes'),
          price: localized('₹2,100', '₹2,100'),
        },
        {
          name: `${t('services.astrology.allKundali')} - ${language === 'hi' ? 'कुंडली सेवाएं' : 'Horoscope Services'}`,
          description: localized(
            'सभी प्रकार की ज्योतिष सेवाएं और कुंडली संबंधी परामर्श।',
            'Comprehensive astrology consultations covering every horoscope need.'
          ),
          benefits: [
            localized('सभी ज्योतिष सेवा', 'All astrology services'),
            localized('व्यक्तिगत परामर्श', 'Personalised consultation'),
            localized('विशेष समाधान', 'Tailored solutions'),
            localized('निरंतर मार्गदर्शन', 'Continuous guidance'),
          ],
          duration: localized('आवश्यकतानुसार', 'As required'),
          price: localized('संपर्क करें', 'Contact for pricing'),
        }
      ]
    },
    {
      id: 'tantrik',
      title: t('services.tantrikTitle'),
      icon: <AutoAwesome />,
      color: '#9C27B0',
      services: [
        {
          name: t('services.tantrik.blackMagic'),
          description: localized(
            'जादू-टोना, काला जादू और नकारात्मक ऊर्जा से पूर्ण सुरक्षा।',
            'Complete protection from black magic, hexes, and negative energies.'
          ),
          benefits: [
            localized('काला जादू से मुक्ति', 'Freedom from black magic'),
            localized('नकारात्मक ऊर्जा हटाना', 'Removes negative energies'),
            localized('सुरक्षा कवच', 'Creates a protective shield'),
            localized('मानसिक शांति', 'Restores mental peace'),
          ],
          duration: localized('2-3 घंटे', '2-3 hours'),
          price: localized('₹5,100', '₹5,100'),
        },
        {
          name: t('services.tantrik.vashikaran'),
          description: localized(
            'वशीकरण मंत्र से प्रेम, विवाह और व्यापारिक समस्याओं का समाधान।',
            'Vashikaran mantras to resolve love, marriage, and business challenges.'
          ),
          benefits: [
            localized('प्रेम समस्या समाधान', 'Solves love-related issues'),
            localized('पति-पत्नी मनमुटाव', 'Resolves marital conflicts'),
            localized('व्यापारिक लाभ', 'Improves business outcomes'),
            localized('शत्रु पर नियंत्रण', 'Gains control over adversaries'),
          ],
          duration: localized('1-2 घंटे', '1-2 hours'),
          price: localized('₹3,100', '₹3,100'),
        },
        {
          name: t('services.tantrik.protection'),
          description: localized(
            'सुरक्षा कवच और बुरी नजर से बचाव के लिए विशेष तंत्र विधान।',
            'Special Tantrik rituals to create a protective shield against evil eye and misfortune.'
          ),
          benefits: [
            localized('बुरी नजर से सुरक्षा', 'Shields against the evil eye'),
            localized('घर की सुरक्षा', 'Protects the household'),
            localized('व्यापार सुरक्षा', 'Secures business ventures'),
            localized('पारिवारिक कल्याण', 'Supports family welfare'),
          ],
          duration: localized('2-3 घंटे', '2-3 hours'),
          price: localized('₹4,100', '₹4,100'),
        },
        {
          name: t('services.tantrik.attraction'),
          description: localized(
            'आकर्षण मंत्र से व्यक्तित्व निखार और सामाजिक प्रभाव बढ़ाना।',
            'Attraction mantras to enhance personality and social influence.'
          ),
          benefits: [
            localized('व्यक्तित्व विकास', 'Develops personality'),
            localized('आकर्षक व्यक्तित्व', 'Makes aura magnetic'),
            localized('सामाजिक प्रभाव', 'Improves social presence'),
            localized('करिश्मा वृद्धि', 'Boosts charisma'),
          ],
          duration: localized('1-2 घंटे', '1-2 hours'),
          price: localized('₹2,500', '₹2,500'),
        },
        {
          name: t('services.tantrik.evil'),
          description: localized(
            'बुरी नजर, नकारात्मकता और अशुभ प्रभावों से मुक्ति दिलाना।',
            'Remove evil eye, negativity and harmful influences effectively.'
          ),
          benefits: [
            localized('नजर दोष निवारण', 'Cures evil eye afflictions'),
            localized('नकारात्मकता हटाना', 'Dispels negativity'),
            localized('मानसिक शुद्धता', 'Purifies the mind'),
            localized('सकारात्मक ऊर्जा', 'Invites positive energy'),
          ],
          duration: localized('1-2 घंटे', '1-2 hours'),
          price: localized('₹2,100', '₹2,100'),
        },
        {
          name: t('services.tantrik.ghost'),
          description: localized(
            'भूत-प्रेत और अशुभ आत्माओं से मुक्ति के लिए विशेष तंत्र क्रिया।',
            'Advanced Tantrik rituals to remove spirits and malevolent entities.'
          ),
          benefits: [
            localized('भूत-प्रेत से मुक्ति', 'Relieves spirit disturbances'),
            localized('घर की शुद्धता', 'Purifies living spaces'),
            localized('मानसिक शांति', 'Brings mental peace'),
            localized('आत्मिक सुरक्षा', 'Provides spiritual protection'),
          ],
          duration: localized('3-4 घंटे', '3-4 hours'),
          price: localized('₹7,100', '₹7,100'),
        }
      ]
    },
    {
      id: 'gemstones',
      title: t('services.gemstoneTitle'),
      icon: <Diamond />,
      color: '#4CAF50',
      services: [
        {
          name: t('services.gemstones.ruby'),
          description: localized(
            'सूर्य ग्रह का रत्न - नेतृत्व, शक्ति और प्रतिष्ठा के लिए।',
            'Gemstone for the Sun – enhances leadership, vitality, and prestige.'
          ),
          benefits: [
            localized('नेतृत्व क्षमता', 'Boosts leadership abilities'),
            localized('आत्मविश्वास', 'Improves confidence'),
            localized('सरकारी कार्य में लाभ', 'Helps in government matters'),
            localized('स्वास्थ्य सुधार', 'Supports physical health'),
          ],
          duration: localized('तुरंत प्रभाव', 'Immediate effect'),
          price: localized('₹15,000 प्रति रत्ती', '₹15,000 per carat'),
        },
        {
          name: t('services.gemstones.pearl'),
          description: localized(
            'चंद्र ग्रह का रत्न - मानसिक शांति और भावनात्मक स्थिरता के लिए।',
            'Moon gemstone – nurtures calmness and emotional balance.'
          ),
          benefits: [
            localized('मानसिक शांति', 'Brings mental peace'),
            localized('भावनात्मक संतुलन', 'Stabilises emotions'),
            localized('मातृ सुख', 'Enhances maternal bliss'),
            localized('सौंदर्य वृद्धि', 'Adds grace and beauty'),
          ],
          duration: localized('तुरंत प्रभाव', 'Immediate effect'),
          price: localized('₹5,000 प्रति रत्ती', '₹5,000 per carat'),
        },
        {
          name: t('services.gemstones.coral'),
          description: localized(
            'मंगल ग्रह का रत्न - साहस, शक्ति और विवाह में देरी का समाधान।',
            'Mars gemstone – energises courage, strength, and removes marriage delays.'
          ),
          benefits: [
            localized('साहस वृद्धि', 'Increases bravery'),
            localized('विवाह में सहायक', 'Helps in timely marriage'),
            localized('रक्त संबंधी रोग', 'Supports blood-related health'),
            localized('भू-संपत्ति लाभ', 'Benefits property matters'),
          ],
          duration: localized('तुरंत प्रभाव', 'Immediate effect'),
          price: localized('₹3,000 प्रति रत्ती', '₹3,000 per carat'),
        },
        {
          name: t('services.gemstones.emerald'),
          description: localized(
            'बुध ग्रह के लिए पन्ना रत्न के फायदे और महत्व।',
            'Mercury gemstone – sharpens intellect and communication.'
          ),
          benefits: [
            localized('बुद्धि बढ़ती है', 'Enhances intelligence'),
            localized('स्मृति सुधारती है', 'Improves memory'),
            localized('वाणी में मधुरता', 'Sweetens speech'),
            localized('व्यापार में लाभ', 'Assists business growth'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹15,000-50,000', '₹15,000-50,000'),
        },
        {
          name: t('services.gemstones.topaz'),
          description: localized(
            'गुरु ग्रह के लिए पोखराज रत्न के दिव्य लाभ।',
            'Jupiter gemstone – attracts wisdom, abundance, and spiritual growth.'
          ),
          benefits: [
            localized('ज्ञान वृद्धि', 'Increases knowledge'),
            localized('धर्म में रुचि', 'Deepens faith'),
            localized('संतान सुख', 'Blesses with progeny happiness'),
            localized('आध्यात्मिक विकास', 'Accelerates spiritual growth'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹20,000-60,000', '₹20,000-60,000'),
        },
        {
          name: t('services.gemstones.sapphire'),
          description: localized(
            'शनि ग्रह के लिए नीलम रत्न का महत्व और लाभ।',
            'Saturn gemstone – stabilises career, finances, and discipline.'
          ),
          benefits: [
            localized('शनि दोष निवारण', 'Mitigates Saturn afflictions'),
            localized('करियर में सफलता', 'Promotes career success'),
            localized('धन लाभ', 'Invites wealth'),
            localized('स्थिरता प्राप्ति', 'Builds stability'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹25,000-1,00,000', '₹25,000-1,00,000'),
        },
        {
          name: t('services.gemstones.gomed'),
          description: localized(
            'राहु ग्रह के लिए गोमेद रत्न के विशेष फायदे।',
            'Rahu gemstone – balances shadow energies and removes obstacles.'
          ),
          benefits: [
            localized('राहु दोष निवारण', 'Neutralises Rahu dosha'),
            localized('तंत्र शक्ति', 'Enhances occult strength'),
            localized('गुप्त शत्रु निवारण', 'Protects from hidden enemies'),
            localized('अचानक लाभ', 'Brings sudden gains'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹10,000-30,000', '₹10,000-30,000'),
        },
        {
          name: t('services.gemstones.catseye'),
          description: localized(
            'केतु ग्रह के लिए लहसुनिया (कैट्स आई) रत्न के लाभ।',
            'Ketu gemstone – heightens intuition and spiritual evolution.'
          ),
          benefits: [
            localized('केतु दोष निवारण', 'Remedies Ketu dosha'),
            localized('आध्यात्मिक शक्ति', 'Enhances spiritual strength'),
            localized('छुपे हुए खजाने', 'Reveals hidden gains'),
            localized('मोक्ष प्राप्ति', 'Supports liberation'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹12,000-35,000', '₹12,000-35,000'),
        },
        {
          name: t('services.gemstones.ruby'),
          description: localized(
            'सूर्य ग्रह के लिए माणिक रत्न के अनुपम लाभ।',
            'Premium ruby gemstone benefits for Sun alignment and prestige.'
          ),
          benefits: [
            localized('आत्मविश्वास वृद्धि', 'Amplifies confidence'),
            localized('नेतृत्व क्षमता', 'Enhances leadership'),
            localized('स्वास्थ्य लाभ', 'Supports vitality'),
            localized('सम्मान प्राप्ति', 'Brings respect and fame'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹30,000-80,000', '₹30,000-80,000'),
        },
        {
          name: t('services.gemstones.pearl'),
          description: localized(
            'चंद्र ग्रह के लिए मोती रत्न के शुभ प्रभाव।',
            'Pure pearl gemstone effects for Moon balance and serenity.'
          ),
          benefits: [
            localized('मानसिक शांति', 'Calms the mind'),
            localized('मातृत्व सुख', 'Enhances nurturing qualities'),
            localized('जल व्यापार लाभ', 'Benefits water-related trades'),
            localized('चंद्र दोष निवारण', 'Resolves Moon dosha'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹5,000-25,000', '₹5,000-25,000'),
        },
        {
          name: t('services.gemstones.coral'),
          description: localized(
            'मंगल ग्रह के लिए मूंगा रत्न का महत्व।',
            'Coral gemstone significance for balancing Mars energy.'
          ),
          benefits: [
            localized('मंगल दोष निवारण', 'Corrects Manglik dosha'),
            localized('साहस वृद्धि', 'Boosts courage'),
            localized('भूमि लाभ', 'Supports property ventures'),
            localized('रक्त संचार सुधार', 'Improves blood circulation'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹8,000-20,000', '₹8,000-20,000'),
        },
        {
          name: t('services.gemstones.diamond'),
          description: localized(
            'शुक्र ग्रह के लिए हीरा रत्न के अद्भुत फायदे।',
            'Venus gemstone – elevates luxury, love, and creativity.'
          ),
          benefits: [
            localized('सौंदर्य वृद्धि', 'Enhances beauty'),
            localized('प्रेम जीवन सुधार', 'Improves love life'),
            localized('कलात्मक प्रतिभा', 'Elevates artistic talent'),
            localized('वैभव प्राप्ति', 'Attracts luxury'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹50,000-2,00,000', '₹50,000-2,00,000'),
        },
        {
          name: t('services.gemstones.sunela'),
          description: localized(
            'सुनहला रत्न के विशेष ज्योतिषीय लाभ और प्रभाव।',
            'Sunela gemstone – brightens aura and strengthens Sun qualities.'
          ),
          benefits: [
            localized('सूर्य शक्ति वृद्धि', 'Intensifies solar energy'),
            localized('स्वास्थ्य लाभ', 'Improves health'),
            localized('तेज वृद्धि', 'Enhances radiance'),
            localized('नेतृत्व क्षमता', 'Supports leadership'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹18,000-45,000', '₹18,000-45,000'),
        },
        {
          name: t('services.gemstones.neelam'),
          description: localized(
            'विशेष नीलम रत्न की शक्ति और लाभकारी प्रभाव।',
            'Exclusive blue sapphire benefits for discipline and justice.'
          ),
          benefits: [
            localized('शनि शांति', 'Pacifies Saturn'),
            localized('करियर उन्नति', 'Advances career'),
            localized('धन संपत्ति', 'Increases assets'),
            localized('न्याय दिलाना', 'Supports fair outcomes'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹28,000-90,000', '₹28,000-90,000'),
        },
        {
          name: t('services.gemstones.panna'),
          description: localized(
            'उत्तम पन्ना रत्न के अनमोल फायदे और प्रभाव।',
            'Premium emerald benefits for Mercury harmony and eloquence.'
          ),
          benefits: [
            localized('बुध ग्रह शांति', 'Balances Mercury'),
            localized('बुद्धि विकास', 'Expands intellect'),
            localized('व्यापार वृद्धि', 'Grows business prospects'),
            localized('वाणी सिद्धि', 'Perfects speech'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹16,000-55,000', '₹16,000-55,000'),
        },
        {
          name: t('services.gemstones.firoza'),
          description: localized(
            'फिरोजा रत्न के विशिष्ट ज्योतिषीय गुण और लाभ।',
            'Turquoise gemstone – ideal for protection, peace, and journeys.'
          ),
          benefits: [
            localized('नेत्र रोग निवारण', 'Helps eye ailments'),
            localized('यात्रा सुरक्षा', 'Ensures travel safety'),
            localized('मानसिक शांति', 'Provides mental calm'),
            localized('आध्यात्मिक शक्ति', 'Strengthens spirituality'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹6,000-18,000', '₹6,000-18,000'),
        },
        {
          name: t('services.gemstones.opal'),
          description: localized(
            'ओपल रत्न के अद्भुत गुण और ज्योतिषीय महत्व।',
            'Opal gemstone – magnifies Venusian charm and creativity.'
          ),
          benefits: [
            localized('शुक्र शक्ति', 'Amplifies Venus energy'),
            localized('कलात्मक प्रतिभा', 'Boosts artistic flair'),
            localized('प्रेम में सफलता', 'Aids love success'),
            localized('सौंदर्य वृद्धि', 'Enhances beauty'),
          ],
          duration: localized('जीवनभर', 'Lifelong effect'),
          price: localized('₹8,000-25,000', '₹8,000-25,000'),
        }
      ]
    }
  ];

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #1976D2 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h1"
            gutterBottom
            sx={{ fontWeight: 700, mb: 2 }}
          >
            {t('services.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto' }}
          >
            {getText(pageCopy.subtitle)}
          </Typography>
        </Container>
      </Box>

      {/* Services Navigation Tabs */}
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Paper elevation={2} sx={{ mb: 4 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                py: 2,
                fontSize: '1rem',
                fontWeight: 500,
              },
            }}
          >
            {serviceCategories.map((category, index) => (
              <Tab
                key={category.id}
                label={category.title}
                icon={category.icon}
                sx={{
                  color: category.color,
                  '&.Mui-selected': {
                    color: category.color,
                  },
                }}
              />
            ))}
          </Tabs>
        </Paper>

        {/* Service Category Content */}
        {serviceCategories.map((category, categoryIndex) => (
          <TabPanel key={category.id} value={selectedTab} index={categoryIndex}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: category.color,
                  textAlign: 'center',
                  mb: 4,
                }}
              >
                {category.title}
              </Typography>

              <Grid container spacing={3} justifyContent="center">
                {category.services.map((service, serviceIndex) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} key={serviceIndex}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                        },
                      }}
                    >
                      <CardMedia
                        sx={{
                          height: 60,
                          backgroundColor: category.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box sx={{ color: 'white', fontSize: 28 }}>
                          {category.icon}
                        </Box>
                      </CardMedia>

                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ fontWeight: 600, color: category.color }}
                        >
                          {service.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {getText(service.description)}
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            {getText(pageCopy.benefitsHeading)}
                          </Typography>
                          <List dense>
                            {service.benefits.map((benefit, benefitIndex) => (
                              <ListItem key={benefitIndex} sx={{ py: 0, px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <Star sx={{ fontSize: 16, color: category.color }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={getText(benefit)}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    fontSize: '0.875rem',
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                          }}
                        >
                          <Chip
                            label={getText(service.duration)}
                            size="small"
                            sx={{
                              backgroundColor: `${category.color}20`,
                              color: category.color,
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: category.color,
                            }}
                          >
                            {getText(service.price)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            flexDirection: { xs: 'column', sm: 'row' },
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                          }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<WhatsApp />}
                            href="https://wa.me/919838167840"
                            target="_blank"
                            sx={{
                              backgroundColor: '#25D366',
                              '&:hover': { backgroundColor: '#128C7E' },
                              flex: '0 0 auto',
                              minWidth: 124,
                              px: 2,
                              py: 0.75,
                              borderRadius: 999,
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              lineHeight: 1.2,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {getText(pageCopy.whatsappCta)}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Phone />}
                            href="tel:+919838167840"
                            sx={{
                              borderColor: category.color,
                              color: category.color,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: category.color,
                                color: 'white',
                              },
                              flex: '0 0 auto',
                              minWidth: 112,
                              px: 2,
                              py: 0.75,
                              borderRadius: 999,
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              lineHeight: 1.2,
                              whiteSpace: 'nowrap',
                              mt: { xs: 1, sm: 0 },
                            }}
                          >
                            {getText(pageCopy.callNow)}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </TabPanel>
        ))}
      </Container>

      {/* FAQ Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 8, mt: 6 }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: 600, mb: 4 }}
          >
            {getText(pageCopy.faqTitle)}
          </Typography>

          {pageCopy.faqItems.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {getText(faq.question)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="text.secondary">
                  {getText(faq.answer)}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Contact CTA */}
      <Box
        sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          py: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2 }}
          >
            {getText(pageCopy.contactHeading)}
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            {getText(pageCopy.contactDescription)}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Phone />}
              href="tel:+919838167840"
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              {getText(pageCopy.phoneButton)}
            </Button>
            
            <Button
              variant="contained"
              size="large"
              startIcon={<WhatsApp />}
              href="https://wa.me/919838167840"
              target="_blank"
              sx={{
                backgroundColor: '#25D366',
                '&:hover': { backgroundColor: '#128C7E' },
              }}
            >
              {getText(pageCopy.whatsappCta)}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Services;